import { openBookACall } from "@/lib/book-a-call";

type Variant = "filled" | "outlined" | "text" | "navlink";
type Size = "md" | "lg";
type Placement = "navbar" | "capabilities" | "footer";

interface BookACallButtonProps {
  variant: Variant;
  placement: Placement;
  /** Defaults to "Talk to us". You demo a tool; you talk to the people who run
   *  your billing. Overriding this back to "Book a demo" re-files Altair as
   *  software, so don't. */
  label?: string;
  /** Defaults to "md". Use "lg" for prominent closing CTAs. */
  size?: Size;
  /** Extra Tailwind classes appended to the variant base. */
  className?: string;
}

const filledClassMd =
  "inline-flex items-center justify-center gap-2 rounded-full text-[14px] font-medium transition-all duration-150 bg-foreground text-background hover:opacity-85 cursor-pointer border-0 whitespace-nowrap";
const filledStyleMd: React.CSSProperties = { padding: "10px 20px" };

const filledClassLg =
  "inline-flex items-center justify-center gap-2 rounded-full text-[16px] font-semibold transition-all duration-150 bg-foreground text-background hover:opacity-85 cursor-pointer border-0 whitespace-nowrap";
const filledStyleLg: React.CSSProperties = { padding: "16px 32px" };

const outlinedClass =
  "inline-flex items-center justify-center gap-2 rounded-full text-[14px] font-medium transition-all duration-150 text-foreground hover:bg-foreground/5 cursor-pointer whitespace-nowrap";
const outlinedStyle: React.CSSProperties = {
  padding: "10px 20px",
  border: "1px solid hsl(var(--primary) / 0.18)",
  background: "transparent",
};

const textClass =
  "text-[14px] no-underline transition-opacity hover:opacity-70 block bg-transparent border-0 cursor-pointer p-0 text-left";
const textStyle: React.CSSProperties = {
  color: "hsl(var(--foreground) / 0.40)",
};

// "navlink" — matches LandingNavbar's text links so Book a demo reads as a
// peer text link, not a button.
const navlinkClass =
  "inline-flex items-center justify-center text-[14px] font-medium text-foreground transition-all duration-150 no-underline bg-transparent border-0 cursor-pointer p-0 hover:opacity-60 whitespace-nowrap";
const navlinkStyle: React.CSSProperties = {};

function getVariantStyles(
  variant: Variant,
  size: Size,
): { className: string; style: React.CSSProperties } {
  if (variant === "outlined") {
    return { className: outlinedClass, style: outlinedStyle };
  }
  if (variant === "text") {
    return { className: textClass, style: textStyle };
  }
  if (variant === "navlink") {
    return { className: navlinkClass, style: navlinkStyle };
  }
  // filled — supports md / lg
  if (size === "lg") {
    return { className: filledClassLg, style: filledStyleLg };
  }
  return { className: filledClassMd, style: filledStyleMd };
}

export function BookACallButton({
  variant,
  placement,
  label = "Talk to us",
  size = "md",
  className,
}: BookACallButtonProps) {
  async function handleClick() {
    await openBookACall(placement, label);
  }

  const { className: baseClass, style } = getVariantStyles(variant, size);

  return (
    <button
      type="button"
      onClick={handleClick}
      className={className ? `${baseClass} ${className}` : baseClass}
      style={style}
      aria-label={label}
    >
      {label}
    </button>
  );
}
